// WorkflowRowActions — the workflow table's Actions cell: a kebab Dropdown of
// the per-workflow actions, rendered in FormWorkflowModal's list. The two tabs
// carry DIFFERENT sets, which the modal expresses purely by which callbacks it
// passes: Workflows gets Duplicate · Version history · Archive, Archived gets
// Version history · Restore · Delete. All are live — Duplicate is POST
// /workflows/:id/duplicate, Version history is GET /workflows/:id/versions (the
// same modal the builder's toolbar opens, reachable here without entering an
// editable surface), and the three lifecycle moves all ride PATCH
// /workflows/:id/status. The flows live in FormWorkflowModal via the callbacks.
//
// Version history is the menu's PERMANENT item — the modal already requires
// workflow `view` to open, so it is always available, and its presence is why
// this kebab can never render empty. The other three are OPTIONAL callbacks:
// the modal passes `undefined` when the verb is missing — or the tab doesn't
// apply — and the item (with any orphaned divider) simply doesn't render.
// Absence means hidden, never disabled.
//
// Modelled on ResponseSetRowActions, the other in-modal kebab: `portal` because
// the table scrolls, so an in-flow panel would be cropped by the overflow
// container, and the whole cell stopPropagations — a row click opens the
// builder, so the menu's clicks must not reach it.

import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface WorkflowRowActionsProps {
  /** Duplicate this workflow. Absent = hidden (no `create`). */
  onDuplicate?: () => void;
  /** Version history. Always present — see the header. */
  onVersions: () => void;
  /** Archive — status → 'archived'. Absent = hidden (the Archived tab). */
  onArchive?: () => void;
  /** Restore — status → 'active'. Archived tab only; absent = hidden. */
  onRestore?: () => void;
  /** Delete — status → 'deleted'. Archived tab only; absent = hidden. */
  onDelete?: () => void;
}

const WorkflowRowActions = ({
  onDuplicate,
  onVersions,
  onArchive,
  onRestore,
  onDelete,
}: WorkflowRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // The row opens the builder on click — keep the menu's clicks out of it.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const item = (
    label: string,
    icon: string,
    onClick: () => void,
    opts: { danger?: boolean } = {},
  ): ReactNode => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <span className='forms-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={200}
        portal
        // A portal panel is fixed-positioned and cannot be scrolled into view,
        // and the list pages up to 50 rows — the last ones would open below the
        // viewport without this.
        flip
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('forms_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {onDuplicate && item(t('forms_action_duplicate'), 'copy', onDuplicate)}
        {item(t('forms_workflow_version_history'), 'history', onVersions)}

        {/* One divider only — two groups is enough structure. It can never lead
            the menu: Version history sits above it on both tabs. */}
        {(onArchive || onRestore || onDelete) && (
          <div className='app-dropdown__divider' role='separator' />
        )}

        {/* Archive and Restore stay untinted because they're reversible; Delete
            is the only item here that isn't. */}
        {onArchive && item(t('forms_action_archive'), 'archive', onArchive)}
        {onRestore && item(t('forms_action_restore'), 'archive-restore', onRestore)}
        {onDelete && item(t('forms_action_delete'), 'trash-2', onDelete, { danger: true })}
      </Dropdown>
    </span>
  );
};

export default WorkflowRowActions;
